"use client";

import { ArrowRight } from "lucide-react";
import {
  siInstagram,
  siX,
  siFacebook,
  siTiktok,
  siWhatsapp,
  siGmail,
  siYoutube,
  siReddit,
  siPinterest,
  siSnapchat,
  siDiscord,
  siGithub,
  siTwitch,
  siSpotify,
  siTelegram,
  siDribbble,
  siBehance,
  siMedium,
  type SimpleIcon,
} from "simple-icons/icons";

import Link from "next/link";
import { m } from "framer-motion";
import Magnetic from "@/components/motion/Magnetic";
import type { Footer as FooterData } from "@/lib/validations/cms";
import EditableText from "@/components/edit-mode/EditableText";

import { useActionState } from "react";
import { subscribeToNewsletter } from "@/lib/email/newsletter";

const SOCIAL_ICONS: Record<string, SimpleIcon> = {
  Instagram: siInstagram,
  Twitter: siX,
  X: siX,
  Facebook: siFacebook,
  TikTok: siTiktok,
  WhatsApp: siWhatsapp,
  Gmail: siGmail,
  YouTube: siYoutube,
  Reddit: siReddit,
  Pinterest: siPinterest,
  Snapchat: siSnapchat,
  Discord: siDiscord,
  GitHub: siGithub,
  Twitch: siTwitch,
  Spotify: siSpotify,
  Telegram: siTelegram,
  Dribbble: siDribbble,
  Behance: siBehance,
  Medium: siMedium,
};

const DEFAULT_ICON = siInstagram;

function iconFor(name: string, iconSlug?: string): SimpleIcon {
  if (iconSlug) {
    const key = Object.keys(SOCIAL_ICONS).find((k) => k.toLowerCase() === iconSlug.toLowerCase());
    if (key && SOCIAL_ICONS[key]) return SOCIAL_ICONS[key];
  }
  return SOCIAL_ICONS[name] ?? DEFAULT_ICON;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  },
};

export default function Footer({ data }: { data: FooterData }) {
  const [state, formAction, isPending] = useActionState(subscribeToNewsletter, {
    message: "",
    success: false,
  });

  const showGrid = !data.disableSquaredPattern;

  return (
    <footer className="border-foreground/10 bg-foreground text-background relative overflow-hidden border-t pt-12 pb-4 md:pt-12 md:pb-6">
      {showGrid && (
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,var(--background)_1px,transparent_1px),linear-gradient(to_bottom,var(--background)_1px,transparent_1px)] bg-[size:40px_40px] opacity-[0.06]" />
      )}

      <div className="relative z-10 mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-8">
        <m.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="mb-6 flex flex-col gap-10 md:mb-12 md:flex-row md:justify-between"
        >
          {data.newsletter.enabled && (
            <m.div variants={itemVariants} className="w-full md:max-w-md">
              <h3 className="mb-4 text-2xl font-black tracking-tighter uppercase md:mb-8 md:text-4xl">
                <EditableText
                  section="footer"
                  light
                  field="newsletter.title"
                  value={data.newsletter.title}
                />
              </h3>
              <p className="text-background/60 mb-6 max-w-md text-sm leading-relaxed font-medium md:mb-8 md:text-lg">
                <EditableText
                  section="footer"
                  light
                  field="newsletter.description"
                  value={data.newsletter.description}
                />
              </p>
              <form action={formAction} className="group relative flex max-w-md flex-col gap-4">
                <div className="group/input relative flex items-center">
                  <input
                    type="email"
                    name="email"
                    placeholder={data.newsletter.placeholder}
                    aria-label="Email address"
                    aria-describedby={state.message ? "newsletter-status" : undefined}
                    autoComplete="email"
                    className="border-background/15 bg-background/5 text-background group-hover/input:border-background/30 placeholder:text-background/40 focus:border-background focus:bg-background/10 w-full border px-4 py-3 pr-28 text-xs tracking-wider transition-colors duration-300 focus:outline-none disabled:opacity-50 md:px-6 md:py-4 md:pr-32 md:text-sm"
                    disabled={isPending}
                  />
                  <Magnetic>
                    <button
                      type="submit"
                      disabled={isPending}
                      className="bg-background text-foreground hover:bg-muted absolute top-1/2 right-1.5 flex h-7 -translate-y-1/2 items-center gap-2 px-3 text-[10px] font-bold tracking-widest uppercase transition-all duration-300 hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-70 md:right-2 md:h-8 md:px-4 md:text-xs"
                    >
                      {isPending ? (
                        "Joining…"
                      ) : (
                        <EditableText
                          section="footer"
                          light
                          field="newsletter.buttonText"
                          value={data.newsletter.buttonText}
                        />
                      )}
                      {!isPending && (
                        <ArrowRight className="h-2.5 w-2.5 transition-transform group-hover:translate-x-1 md:h-3 md:w-3" />
                      )}
                    </button>
                  </Magnetic>
                </div>
                {state.message && (
                  <p
                    id="newsletter-status"
                    role="status"
                    aria-live="polite"
                    className={`ml-1 text-xs ${state.success ? "text-green-500" : "text-destructive"}`}
                  >
                    {state.message}
                  </p>
                )}
              </form>
            </m.div>
          )}

          <m.div
            variants={containerVariants}
            className="grid grid-cols-2 gap-x-12 gap-y-10 px-8 sm:px-12 md:flex md:gap-20 lg:gap-32 lg:px-15"
          >
            {data.shop.enabled && (
              <m.div variants={itemVariants} className="col-span-1">
                <h3 className="text-background/60 mb-6 flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase md:mb-8">
                  <span className="bg-background/60 h-1 w-1 rounded-full"></span>{" "}
                  <EditableText section="footer" light field="shop.title" value={data.shop.title} />
                </h3>
                <ul className="space-y-3 md:space-y-4">
                  {data.shop.items.map((item) => (
                    <li key={item}>
                      <Magnetic>
                        <Link
                          href={`/products/category/${item.toLowerCase().replace(/ /g, "-")}`}
                          className="hover-underline-animation text-background/60 hover:text-background inline-block text-sm font-bold tracking-tight uppercase transition-colors md:text-lg"
                        >
                          <EditableText section="footer" light field="shop.items" value={item} />
                        </Link>
                      </Magnetic>
                    </li>
                  ))}
                </ul>
              </m.div>
            )}

            {data.support.enabled && (
              <m.div variants={itemVariants} className="col-span-1">
                <h3 className="text-background/60 mb-6 flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase md:mb-8">
                  <span className="bg-background/60 h-1 w-1 rounded-full"></span>{" "}
                  <EditableText
                    section="footer"
                    light
                    field="support.title"
                    value={data.support.title}
                  />
                </h3>
                <ul className="space-y-3 md:space-y-4">
                  {data.support.items.map((item, index) => {
                    const label = typeof item === "string" ? item : item.label;
                    const href = typeof item === "string" ? `/${item.toLowerCase()}` : item.href;
                    return (
                      <li key={label}>
                        <Magnetic>
                          <Link
                            href={href}
                            className="hover-underline-animation text-background/60 hover:text-background inline-block text-sm font-bold tracking-tight uppercase transition-colors md:text-lg"
                          >
                            <EditableText
                              section="footer"
                              light
                              field={`support.items.${index}`}
                              value={label}
                            />
                          </Link>
                        </Magnetic>
                      </li>
                    );
                  })}
                </ul>
              </m.div>
            )}

            {data.social.enabled && (
              <m.div variants={itemVariants} className="col-span-2 md:w-auto">
                <h3 className="text-background/60 mb-6 flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase md:mb-8">
                  <span className="bg-background/60 h-1 w-1 rounded-full"></span>{" "}
                  <EditableText
                    section="footer"
                    light
                    field="social.title"
                    value={data.social.title}
                  />
                </h3>

                <ul className="hidden space-y-4 md:block">
                  {data.social.items.map((social) => (
                    <li key={social.name}>
                      <a
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group text-background/60 hover:text-background flex items-center gap-3 text-lg font-bold tracking-tight uppercase transition-colors"
                        aria-label={social.name}
                      >
                        <svg
                          role="img"
                          viewBox="0 0 24 24"
                          className="h-5 w-5 fill-current transition-transform duration-300 group-hover:scale-110"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d={iconFor(social.name, social.iconSlug).path} />
                        </svg>{" "}
                        <EditableText
                          section="footer"
                          light
                          field={`social.items.${data.social.items.indexOf(social)}.name`}
                          value={social.name}
                        />
                      </a>
                    </li>
                  ))}
                </ul>

                <div className="flex gap-6 md:hidden">
                  {data.social.items.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group border-background/15 bg-background/5 text-background/60 hover:border-background hover:bg-background hover:text-foreground flex items-center justify-center rounded-full border p-3 transition-colors"
                      aria-label={social.name}
                    >
                      <svg
                        role="img"
                        viewBox="0 0 24 24"
                        className="h-5 w-5 fill-current"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d={iconFor(social.name, social.iconSlug).path} />
                      </svg>
                    </a>
                  ))}
                </div>
              </m.div>
            )}
          </m.div>
        </m.div>

        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            delay: 0.4,
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1] as const,
          }}
          className="border-background/10 flex flex-col items-center justify-between gap-6 border-t pt-8 md:flex-row md:pt-12"
        >
          <p className="text-background/40 font-mono text-[10px] tracking-widest md:text-xs">
            <EditableText section="footer" light field="copyright" value={data.copyright} />
          </p>
          <div className="flex gap-6 md:gap-8">
            <Link
              href="/privacy"
              className="text-background/40 hover:text-background font-mono text-[10px] tracking-widest uppercase transition-colors md:text-xs"
            >
              <EditableText section="footer" light field="privacyText" value={data.privacyText} />
            </Link>
            <Link
              href="/terms"
              className="text-background/40 hover:text-background font-mono text-[10px] tracking-widest uppercase transition-colors md:text-xs"
            >
              <EditableText section="footer" light field="termsText" value={data.termsText} />
            </Link>
          </div>
        </m.div>
      </div>
    </footer>
  );
}
